// src/pages/reprint/Reprint.jsx
import React, { useState, useEffect } from 'react';
import '@assets/css/reprint/style_reprint.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_calendar.css';
import Calendar from '@components/common/Calendar.jsx';
import PaginationZeroBased from '@components/common/PaginationZeroBased.jsx';
import printApi from '@services/printApi.jsx';
import branchApi from '@services/branchApi.jsx';
import { useAuthStore } from '@store/useAuthStore.jsx';

function Reprint() {
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedBranchCode, setSelectedBranchCode] = useState('all');
  const [branchOptions, setBranchOptions] = useState([]);

  const { user, isAdmin } = useAuthStore();
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });
  const [activeInput, setActiveInput] = useState(null);
  
  // 프린트 요청 데이터 상태
  const [printRequests, setPrintRequests] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  /**
   * 개발 환경에서 프록시 서버를 통한 이미지 URL 가져오기
   */
  const getProxyImageUrl = (originalUrl) => {
    if (!originalUrl) return originalUrl;
    
    // 개발 환경에서만 프록시 서버를 통해 이미지 로드
    if (import.meta.env.VITE_ENV === 'dev') {
      // pub-image.postme.co.kr을 image.postme.co.kr로 변경하고 프록시 서버 사용
      return `https://file-worker.postme.co.kr/download?path=${originalUrl}`;
    }
    return originalUrl;
  };

  // 상태별 한글 표시를 위한 매핑
  const getStatusText = (status) => {
    const statusMap = {
      'PENDING': '대기중',
      'PROCESSING': '처리중',
      'COMPLETED': '완료',
      'FAILED': '실패',
      'CANCELLED': '취소됨'
    };
    return statusMap[status] || status;
  };

  // 요청 타입별 한글 표시를 위한 매핑
  const getRequestTypeText = (type) => {
    const typeMap = {
      'PHOTO': '사진',
      'QR': 'QR'
    };
    return typeMap[type] || type;
  };

  // 날짜 포맷팅 함수 (두 줄로 표시: 연/월/일 + 시:분:초)
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';

    try {
      const date = new Date(dateTimeString);

      // 유효한 날짜인지 확인
      if (isNaN(date.getTime())) {
        return '-';
      }

      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hour = date.getHours().toString().padStart(2, '0');
      const minute = date.getMinutes().toString().padStart(2, '0');
      const second = date.getSeconds().toString().padStart(2, '0');

      // 두 줄로 나누기 위해 <br> 태그 사용
      return (
        <>
          <div>{`${year}/${month}/${day}`}</div>
          <div>{`${hour}:${minute}:${second}`}</div>
        </>
      );
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return '-';
    }
  };

  // 프린트 요청 목록 조회
  const fetchPrintRequests = async (page = 0) => {
    setIsLoading(true);
    try {
      const params = {
        page: page,
        size: pagination.size,
        sortDirection: 'desc'
      };

      // 날짜 필터가 있는 경우 추가
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }

      // 지점 필터가 있는 경우 추가
      if (selectedBranchCode && selectedBranchCode !== 'all') {
        params.branchCode = selectedBranchCode;
      }

      const response = await printApi.getPrintRequests(params);
      setPrintRequests(response.content || []);
      setPagination({
        page: response.page || 0,
        size: response.size || 10,
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
        hasNext: response.hasNext || false,
        hasPrevious: response.hasPrevious || false
      });
    } catch (error) {
      console.error('프린트 요청 목록 조회 실패:', error);
      setPrintRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadBranchOptions();
  }, [user]);

  useEffect(() => {
    fetchPrintRequests();
  }, []);

  /**
   * 권한에 따른 지점 목록 로드
   */
  const loadBranchOptions = async () => {
    try {
      if (isAdmin()) {
        // ADMIN: 전체 지점 목록 조회
        const response = await branchApi.getBranches({ size: 1000 });
        const branches = response.data?.content || [];
        setBranchOptions(branches.map(branch => ({
          branchCode: branch.branchCode,
          name: branch.name
        })));
      } else {
        // MANAGER/USER: 할당된 지점만 표시
        if (user?.userRoles) {
          const assignedBranches = user.userRoles
            .filter(role => role.branchId !== null && role.branchCode)
            .map(role => ({
              branchCode: role.branchCode,
              name: role.branchName
            }));
          // 중복 제거
          const uniqueBranches = assignedBranches.filter((branch, index, self) =>
            index === self.findIndex(b => b.branchCode === branch.branchCode)
          );
          setBranchOptions(uniqueBranches);
        }
      }
    } catch (error) {
      console.error('지점 목록 조회 실패:', error);
    }
  };

  // 날짜 선택 핸들러
  const handleDateSelect = (selectedStartDate, selectedEndDate) => {
    const formatDate = (date) => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}${month}${day}`;
    };

    const formattedDate = formatDate(selectedStartDate);
    
    // 활성화된 입력 필드에 따라 다르게 처리
    if (activeInput === 'start') {
      setStartDate(formattedDate);
    } else if (activeInput === 'end') {
      setEndDate(formattedDate);
    }
    
    setShowCalendar(false);
    setActiveInput(null);
  };

  // 입력 필드 클릭 핸들러
  const handleInputClick = (inputType, event) => {
    const input = event.target;
    const rect = input.getBoundingClientRect();
    
    setCalendarPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left
    });
    
    setActiveInput(inputType);
    setShowCalendar(true);
  };

  // 행 클릭 핸들러
  const handleRowClick = (item) => {
    setSelectedItem(item);
  };

  // 검색 버튼 핸들러
  const handleSearch = () => {
    fetchPrintRequests(0); // 첫 페이지부터 다시 검색
  };

  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      fetchPrintRequests(newPage);
    }
  };

  // 인쇄 버튼 핸들러
  const handlePrint = () => {
    if (!selectedItem) {
      alert('인쇄할 항목을 선택해주세요.');
      return;
    }
    setShowPrintModal(true);
  };

  // 인쇄 확인 핸들러
  const handlePrintConfirm = async () => {
    try {
      setIsLoading(true);
      
      // 선택된 항목에 대해 프린트 요청 재시도
      await printApi.retryPrintRequest(selectedItem.id);
      
      alert('선택한 항목의 인쇄가 요청되었습니다.');
      setShowPrintModal(false);
      
      // 목록 새로고침
      fetchPrintRequests(pagination.page);
      
    } catch (error) {
      console.error('인쇄 요청 실패:', error);
      alert('인쇄 요청 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 로딩 상태 변경시 페이지 로더 표시 처리
  useEffect(() => {
    const pageLoader = document.querySelector('.page-loader');
    if (pageLoader) {
      if (isLoading) {
        pageLoader.classList.add('active');
      } else {
        pageLoader.classList.remove('active');
      }
    }
  }, [isLoading]);

  // 전체 선택 체크박스 상태 업데이트 - 삭제

  return (
    <>
      {/* 리프린트 콘텐츠 시작 */}
      <div className="page-title-container">
        <h1 className="page-title">리프린트</h1>
      </div>

      <div className="reprint-container">
        {/* 좌측 테이블 영역 */}
        <div className="reprint-table-section">
          {/* 필터 영역 */}
          <div className="filter-container data-section-filter">
            <h3 className="section-title">프린트 요청 기록</h3>
            <div className="filter-controls-container">
              <div className="date-filter">
                <input 
                  type="text" 
                  className={`date-input ${activeInput === 'start' ? 'active' : ''}`}
                  id="start-date" 
                  placeholder="시작일" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                  onClick={(e) => handleInputClick('start', e)}
                  readOnly
                />
                <span>-</span>
                <input 
                  type="text" 
                  className={`date-input ${activeInput === 'end' ? 'active' : ''}`}
                  id="end-date" 
                  placeholder="종료일" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)}
                  onClick={(e) => handleInputClick('end', e)}
                  readOnly
                />
              </div>
              <select
                className="filter-select"
                value={selectedBranchCode}
                onChange={(e) => setSelectedBranchCode(e.target.value)}
              >
                <option value="all">전체 지점</option>
                {branchOptions.map((branch) => (
                  <option key={branch.branchCode} value={branch.branchCode}>
                    {branch.name} ({branch.branchCode})
                  </option>
                ))}
              </select>
              <button className="search-btn" onClick={handleSearch}>검색</button>
            </div>
          </div>

          {/* 테이블 */}
          <div className="common-table-container">
            <table className="common-table">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}>NO</th>
                  <th>UUID</th>
                  <th>BranchCode</th>
                  <th>RobotNo</th>
                  <th>요청일시</th>
                  <th>타입</th>
                  <th>수량</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {printRequests.length > 0 ? (
                  printRequests.map((item, index) => (
                    <tr 
                      key={item.id} 
                      onClick={() => handleRowClick(item)}
                      className={`table-row ${selectedItem?.id === item.id ? 'selected' : ''}`}
                      style={{ cursor: 'pointer' }}
                    >
                      <td style={{ width: '50px' }}>{pagination.page * pagination.size + index + 1}</td>
                      <td>{item.uuid || '-'}</td>
                      <td>{item.branchCode || ''}</td>
                      <td>{item.robotNo || ''}</td>
                      <td style={{ whiteSpace: 'normal', lineHeight: '1.4' }}>{formatDateTime(item.requestedAt)}</td>
                      <td>{getRequestTypeText(item.requestType)}</td>
                      <td>{item.amount || '-'}</td>
                      <td>
                        <span className={`status-badge status-${item.requestStatus?.toLowerCase()}`}>
                          {getStatusText(item.requestStatus)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                      {isLoading ? '데이터를 불러오는 중...' : '프린트 요청 기록이 없습니다.'}
                    </td>
                  </tr>
                )}
                
                {/* 빈 행으로 테이블 높이 유지 */}
                {printRequests.length > 0 && printRequests.length < 10 && 
                  Array(10 - printRequests.length).fill().map((_, index) => (
                    <tr key={`empty-${index}`}>
                      <td colSpan="8">&nbsp;</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>

          {/* 페이지네이션 */}
          <PaginationZeroBased
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalElements={pagination.totalElements}
            size={pagination.size}
            hasNext={pagination.hasNext}
            hasPrevious={pagination.hasPrevious}
            onPageChange={handlePageChange}
          />
        </div>

        {/* 우측 프리뷰 영역 */}
        <div className="reprint-preview-container">
          <h3 className="section-title">수동처리</h3>
          <div className="preview-image-container">
            {selectedItem ? (
              <>
                <span className="preview-image-title">{getRequestTypeText(selectedItem.requestType)}</span>
                {selectedItem.requestType === 'PHOTO' ? (
                  selectedItem.filePath ? (
                    <img 
                      src={getProxyImageUrl(selectedItem.filePath)} 
                      alt="사진 이미지" 
                      className="preview-image" 
                    />
                  ) : (
                    <div className="no-image-container">
                      <p className="no-image-text">이미지를 찾을 수 없습니다</p>
                    </div>
                  )
                ) : (
                  selectedItem.filePath ? (
                    <img 
                      src={getProxyImageUrl(selectedItem.filePath)} 
                      alt="QR 코드" 
                      className="qr-image" 
                    />
                  ) : (
                    <div className="no-image-container">
                      <p className="no-image-text">QR 코드를 찾을 수 없습니다</p>
                    </div>
                  )
                )}
              </>
            ) : (
              <div className="no-selection-container">
                <p className="no-selection-text">항목을 선택해주세요</p>
              </div>
            )}
          </div>
          <div className="common-button-container">
            <button 
              className={`common-button ${!selectedItem ? 'disabled' : ''}`}
              id="print-btn" 
              onClick={handlePrint}
              disabled={!selectedItem}
            >
              인쇄하기 {selectedItem ? '(1)' : '(0)'}
            </button>
          </div>
        </div>
      </div>

      {/* 인쇄 모달 */}
      {showPrintModal && (
        <div className="modal-overlay active" id="print-modal">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">재인쇄 확인</h3>
              <button className="modal-close" onClick={() => setShowPrintModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>선택한 {getRequestTypeText(selectedItem?.requestType)} 항목을 재인쇄하시겠습니까?</p>
              <p className="modal-sub-text">재인쇄 요청이 프린트 대기열에 추가됩니다.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn modal-btn-cancel" 
                onClick={() => setShowPrintModal(false)}
              >
                취소
              </button>
              <button 
                className="modal-btn modal-btn-primary"
                onClick={handlePrintConfirm}
              >
                재인쇄
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 캘린더 */}
      {showCalendar && (
        <Calendar
          onDateSelect={handleDateSelect}
          onClose={() => {
            setShowCalendar(false);
            setActiveInput(null);
          }}
          position={calendarPosition}
        />
      )}
    </>
  );
}

export default Reprint;